/**
 * A country's flag.
 *
 * **Not the emoji.** `🇳🇬` is two regional-indicator letters that a font is expected to draw as a
 * flag, and Windows ships no font that does — every Windows browser renders it as the letters
 * `NG` in a box. That is most of the desktop, so the emoji is not an option for a control people
 * have to recognise at a glance.
 *
 * So the artwork is real: the `flag-icons` SVG set, resolved to a URL at build time. Vite emits
 * each flag as its own file, which means the bundle carries 271 short strings rather than 2.4MB
 * of artwork, and a page fetches only the two or three flags it actually shows.
 *
 * A code with no artwork falls back to the letters themselves, which is still the answer to
 * "which country is this" — never to a broken image.
 */

const FLAGS = import.meta.glob<string>("../../../../node_modules/flag-icons/flags/4x3/*.svg", {
  query: "?url",
  import: "default",
  eager: true,
});

/** `NG` → the URL of its flag, or null when the set has no artwork for it. */
function flagUrl(code: string): string | null {
  const key = code.trim().toLowerCase();
  for (const [path, url] of Object.entries(FLAGS)) {
    if (path.endsWith(`/${key}.svg`)) return url;
  }
  return null;
}

/** Built once: 271 entries, looked up by code rather than scanned. */
const BY_CODE = new Map<string, string>(
  Object.entries(FLAGS).map(([path, url]) => [path.slice(path.lastIndexOf("/") + 1, -4), url]),
);

export function Flag({
  country,
  className = "",
  title,
}: {
  /** ISO 3166-1 alpha-2, in either case. */
  country: string;
  className?: string;
  title?: string;
}) {
  const code = country.trim().toLowerCase();
  const url = BY_CODE.get(code) ?? flagUrl(code);

  if (!url) {
    return (
      <span
        aria-hidden={title ? undefined : true}
        title={title}
        className={`inline-flex items-center justify-center rounded-[2px] bg-ink-100 text-[9px] font-semibold uppercase leading-none text-ink-600 ${className}`}
      >
        {code}
      </span>
    );
  }

  return (
    <img
      src={url}
      alt={title ?? ""}
      title={title}
      loading="lazy"
      // A hairline, because several flags are white at the edge and would otherwise bleed into a
      // white field with no border of their own.
      className={`inline-block shrink-0 rounded-[2px] object-cover ring-1 ring-ink-900/10 ${className}`}
    />
  );
}
